import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import VMService from "../../../services/VMService";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";

const LabActivityContext = createContext();

export const LabActivityProvider = ({ children }) => {

  const location = useLocation();
  const authContext = useContext(AuthContext);

  const [warningSeconds, setWarningSeconds] = useState(0);
  const [isLabRunning, setIsLabRunning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleStage, setIdleStage] = useState("active");

  const labStartedAtRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningStartRef = useRef(null);

  const resetActivity = () => {
    const now = Date.now();

    lastActivityRef.current = now;
    warningStartRef.current = null;

    localStorage.setItem("lab_last_activity", now.toString());

    setShowIdleWarning(false);
    setWarningSeconds(0);
    setIdleStage("active");
  };

  // checkbackend
  useEffect(() => {

  const checkBackendStatus = async () => {

    const savedCourse = localStorage.getItem("running_lab_course");
    if (!savedCourse) return;

    const course = JSON.parse(savedCourse);

    const result = await VMService.statusVM(course.id, authContext);

    if (!result?.data?.isVMRunning) {

      localStorage.removeItem("running_lab_course");
      localStorage.removeItem("lab_end_time");

      setIsLabRunning(false);
      setRemainingSeconds(0);

    }

  };

  checkBackendStatus();

}, [authContext]);

  // USER ACTIVITY
  useEffect(() => {
    const updateActivity = () => {
      if (isLabRunning && location.pathname.startsWith("/bay")) {
        resetActivity();
      }
    };

    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("click", updateActivity);

    return () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
    };
  }, [isLabRunning, location.pathname]);

  // VM iframe activity
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "vm-activity") {
        resetActivity();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // TAB visibility
  useEffect(() => {
    const handleVisibility = () => {
if (
  document.visibilityState === "visible" &&
  location.pathname.startsWith("/bay")
) {
  resetActivity();
}
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // Cross tab activity sync
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === "lab_last_activity") {
        const time = parseInt(event.newValue);

        if (!isNaN(time)) {
          lastActivityRef.current = time;
          warningStartRef.current = null;
          setShowIdleWarning(false);
          setIdleStage("active");
        }
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // VM EVENTS (TAB CLOSED / STOP)
useEffect(() => {

  const handleStorage = (event) => {
      if (location.pathname === "/bay") return;

    if (event.key !== "vm_event") return;

    const data = JSON.parse(event.newValue || "{}");
    if (
      data.type === "TAB_CLOSED" &&
      localStorage.getItem("running_lab_course")
    ) {

      localStorage.setItem("vm_tab_reloading", "true");

      setTimeout(async () => {
        const val = localStorage.getItem("vm_tab_reloading");
        if (val === "true") {
          const savedCourse = localStorage.getItem("running_lab_course");
          if (!savedCourse) return;
          const course = JSON.parse(savedCourse);
          if (!course?.id) return;
          const result = await VMService.stopVM(course.id, authContext);
          if (result?.isSuccess) {
            localStorage.removeItem("running_lab_course");
            localStorage.removeItem("lab_end_time");
            setIsLabRunning(false);
            setShowIdleWarning(false);
            setIdleStage("active");
            localStorage.setItem(
              "vm_event",
              JSON.stringify({
                type: "STOP",
                time: Date.now()
              })
            );
            AlertService.success(
              "Lab session terminated because VM tab was closed."
            );

          } else {
            AlertService.error(
              result?.errorMessage || "Failed to stop the lab session."
            );

          }

        }

      }, 800);
    }

  };

  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);

}, [authContext, location.pathname]);

  // IDLE LOGIC
  useEffect(() => {

    const WARNING_TIME = 10 * 60 * 1000; // 10 mins idle
    const SHUTDOWN_TIME = 5 * 60 * 1000;

    const interval = setInterval(() => {

      if (!isLabRunning) return;

      const now = Date.now();
      const idleTime = now - lastActivityRef.current;
      
      // Stage 1 - Warning
      if (idleTime >= WARNING_TIME && idleStage === "active") {
        setIdleStage("warning");
        setShowIdleWarning(true);
        warningStartRef.current = now;
        setWarningSeconds(300);
        return;
      }

      // Stage 2 - Shutdown
      if (
        idleStage === "warning" &&
        warningStartRef.current &&
        now - warningStartRef.current >= SHUTDOWN_TIME
      ) {
        setIdleStage("shutdown");

        localStorage.setItem(
          "lab_force_shutdown",
          JSON.stringify({ time: now })
        );
      }

    }, 5000);

    return () => clearInterval(interval);

  }, [isLabRunning, idleStage]);

  // WARNING COUNTDOWN
  useEffect(() => {

    if (idleStage !== "warning") return;

    const interval = setInterval(() => {

      setWarningSeconds(prev => {

        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;

      });

    }, 1000);

    return () => clearInterval(interval);

  }, [idleStage]);

  // LAB START RESET
  useEffect(() => {

    if (isLabRunning) {
      labStartedAtRef.current = Date.now();
      resetActivity();
    }
    else {
      labStartedAtRef.current = null;
    }

  }, [isLabRunning]);

  // GLOBAL LAB TIMER
  useEffect(() => {

    if (!isLabRunning) return;

    let endTime = localStorage.getItem("lab_end_time");

    if (endTime) {
      endTime = parseInt(endTime);
    }
    else {
      endTime = Date.now() + remainingSeconds * 1000;
      localStorage.setItem("lab_end_time", endTime.toString());
    }

    const interval = setInterval(() => {

      const secondsLeft = Math.max(
        0,
        Math.floor((endTime - Date.now()) / 1000)
      );

      setRemainingSeconds(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(interval);
        localStorage.removeItem("lab_end_time");
      }

    }, 1000);

    return () => clearInterval(interval);

  }, [isLabRunning]);

  // RESTORE TIMER AFTER REFRESH
  useEffect(() => {

    const savedCourse = localStorage.getItem("running_lab_course");
    const endTime = localStorage.getItem("lab_end_time");

    if (savedCourse && endTime) {

      const secondsLeft = Math.max(
        0,
        Math.floor((parseInt(endTime) - Date.now()) / 1000)
      );

      if (secondsLeft > 0) {

        setRemainingSeconds(secondsLeft);
        setIsLabRunning(true);

      }
    }

  }, []);

  useEffect(() => {

  const handleVMEvent = (event) => {

    if (event.key !== "vm_event") return;

    const data = JSON.parse(event.newValue || "{}");

 if (data.type === "STOP") {
  setIsLabRunning(false);
  setShowIdleWarning(false);
  setIdleStage("active");
    setWarningSeconds(0);
      localStorage.removeItem("lab_end_time");

}
  };

  window.addEventListener("storage", handleVMEvent);

  return () =>
    window.removeEventListener("storage", handleVMEvent);

}, []);

  // IDLE SHUTDOWN
  useEffect(() => {

    if (idleStage !== "shutdown") return;
    if (!isLabRunning) return;

    const savedCourse = localStorage.getItem("running_lab_course");
    if (!savedCourse) return;

    const course = JSON.parse(savedCourse);

    VMService.stopVM(course.id, authContext)
      .then(() => {

        setIsLabRunning(false);
        setShowIdleWarning(false);
        setIdleStage("active");

        localStorage.removeItem("running_lab_course");
        localStorage.removeItem("lab_end_time");

        localStorage.setItem(
          "vm_event",
          JSON.stringify({
            type: "STOP",
            time: Date.now()
          })
        );

      })
      .catch(err => {
        console.error("Auto stop failed", err);
      });

  }, [idleStage, isLabRunning, authContext]);

  return (
    <LabActivityContext.Provider
      value={{
        isLabRunning,
        setIsLabRunning,
        remainingSeconds,
        setRemainingSeconds,
        showIdleWarning,
        setShowIdleWarning,
        idleStage,
        warningSeconds,
      }}
    >
      {children}
    </LabActivityContext.Provider>
  );
};

export const useLabActivity = () => useContext(LabActivityContext);